import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabPane,
  InputGroupText,
  InputGroupAddon,
  InputGroup,
  TabContent,
} from "reactstrap";
import { showAlert } from "components/Common/commonFunctions";
import Breadcrumbs from "components/Common/BreadCrumbs";
import { getUserByID, editUser, changeUserPassword } from "actions/subUser";
import { useDispatch, useSelector } from "react-redux";
import { REGEX } from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { urlDecodedId } from "components/Common/encodeDecodeFunction";
import { LocationTable } from "./LocationTable";

const initialFields = {
  firstName: {
    value: "",
    error: "",
  },
  lastName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  phone: {
    value: "",
    error: "",
  },
};

const changePasswordFields = {
  password: {
    value: "",
    error: "",
  },
  confirmPassword: {
    value: "",
    error: "",
  },
};

const UserDetails = () => {
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  // Parse query params to get edit flag
  const queryParams = new URLSearchParams(location.search);
  const [user, setUser] = useState(null); //for storing the non saved part
  const [userFormData, setUserFormData] = useState(initialFields);
  const [userPasswordData, setPasswordData] = useState(changePasswordFields);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  const query = new URLSearchParams(location.search);
  const urlTab = query.get("tab");
  const isEditFromUrl =
    queryParams.get("edit") === "true" && (urlTab === "details" || !urlTab);
  const [isEditing, setIsEditing] = useState(isEditFromUrl);
  const [activeTab, setActiveTab] = useState(urlTab || "details");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [showLoaderPasswordSave, setShowLoaderOnPasswordSave] = useState(false);
  const [isAPiProcessing, setIsAPiProcessing] = useState(false);
  const [assignedLocationIds, setAssignedLocationIds] = useState([]);

  useEffect(() => {
    if (id) {
      getUserDetails(decodedId);
    }
  }, [decodedId]);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const getUserDetails = async (id) => {
    try {
      setShowAlternative({ loader: true, error: false });
      const res = await getUserByID(id);
      if (res.code === 200) {
        const userDetails = res?.data;
        setUser(userDetails);
        setUserFormData({
          firstName: { value: userDetails?.firstName || "", error: "" },
          lastName: { value: userDetails?.lastName || "", error: "" },
          email: { value: userDetails?.email || "", error: "" },
          phone: { value: userDetails?.phone || "", error: "" },
        });
        setShowAlternative({ loader: false, error: false });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to load user details!";
        setShowAlternative({ loader: false, error: message });
      }
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: error || "Failed to load user details!",
      });
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set("tab", tab);

      setIsEditing(false);
      searchParams.delete("edit");


      if (tab === "change_password") {
        setUserFormData((prev) => ({
          firstName: { ...prev.firstName, error: "" },
          lastName: { ...prev.lastName, error: "" },
          email: { ...prev.email, error: "" },
          phone: { ...prev.phone, error: "" },
        }));
      } else {
        setPasswordData(changePasswordFields);
      }

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }

    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleInputChange = (fieldName, newValue) => {
    setUserFormData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  const handlePasswordInputChange = (fieldName, newValue) => {
    setPasswordData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };
  // based on the field name will retuen error if condition meets
  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter a first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter a last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "email":
        return !value
          ? "Please enter an email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid email"
            : "";

      case "phone":
        return !value
          ? "Please enter a contact number"
          : !REGEX.CONTACT_REGEX.test(value)
            ? "Please enter a valid 9-digit contact number"
            : "";
      default:
        return "";
    }
  };

  const getValidationErrorPassword = (name, value) => {
    switch (name) {
      case "password":
        return !value
          ? "Please enter a password"
          : value.length < 8
            ? "The password must contain 8 characters"
            : userPasswordData.confirmPassword.value &&
              value !== userPasswordData.confirmPassword.value
              ? "The new password and confirm password must match"
              : "";
      case "confirmPassword":
        return !value
          ? "Please enter a confirm password"
          : userPasswordData.password.value &&
            value !== userPasswordData.password.value
            ? "The new password and confirm password must match"
            : "";
      default:
        break;
    }
  };

  //to validate form for all the keys in the userForm object
  const validateForm = () => {
    let newUser = { ...userFormData };

    // get all keys in array and then iterate
    Object.keys(newUser).forEach((key) => {
      newUser[key] = {
        ...newUser[key],
        error: getValidationError(key, newUser[key].value),
      };
    });

    setUserFormData(newUser); // set the error state

    //if no error exisit then return true else false
    return Object.values(newUser).every((item) => item.error === "");
  };

  const validatePasswordForm = () => {
    let newPassword = { ...userPasswordData };

    // get all keys in array and then iterate
    Object.keys(newPassword).forEach((key) => {
      newPassword[key] = {
        ...newPassword[key],
        error: getValidationErrorPassword(key, newPassword[key].value),
      };
    });

    setPasswordData(newPassword); // set the error state

    //if no error exisit then return true else false
    return Object.values(newPassword).every((item) => item.error === "");
  };

  const handleSaveClick = async () => {
    if (validateForm()) {
      try {
        setShowLoaderOnClick(true);
        // bodyData which will se sent to api
        const bodyData = {
          firstName: userFormData.firstName.value,
          lastName: userFormData.lastName.value,
          email: userFormData.email.value,
          phone: userFormData.phone.value,
        };
        const res = await editUser(decodedId, bodyData);
        if (res.code === 200) {
          notify("success", res.message || "User updated successfully");
          await getUserDetails(decodedId);

          setIsEditing(false);
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete("edit");
          navigate(`${location.pathname}?${searchParams.toString()}`, {
            replace: true,
          });
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg
            : res?.message || "Failed to update User!";
          notify("danger", message);
        }
      } catch (e) {
        notify("danger", "Failed to update user!");
      } finally {
        setShowLoaderOnClick(false);
      }
    }
  };

  const handlePasswordSaveClick = async () => {
    if (validatePasswordForm()) {
      try {
        setShowLoaderOnPasswordSave(true);
        // bodyData which will se sent to api
        const bodyData = {
          newPassword: userPasswordData.password.value,
          confirmNewPassword: userPasswordData.confirmPassword.value,
        };
        const res = await changeUserPassword(decodedId, bodyData);
        if (res.code === 200) {
          notify("success", res.message || "Password changed successful");
          setPasswordData(changePasswordFields);
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg
            : res?.message || "Failed to change password!";
          notify("danger", message);
        }
      } catch (e) {
        notify("danger", "Failed to change password!");
      } finally {
        setShowLoaderOnPasswordSave(false);
      }
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setUserFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleOnBlurPassForm = (name, value) => {
    const error = getValidationErrorPassword(name, value);
    setPasswordData((prev) => ({
      ...prev,
      [name]: { ...prev[name], error },
    }));
  };

  // Handle Cancel button click
  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data back to original user details
    setUserFormData({
      firstName: { value: user?.firstName || "", error: "" },
      lastName: { value: user?.lastName || "", error: "" },
      email: { value: user?.email || "", error: "" },
      phone: { value: user?.phone || "", error: "" },
    });
    // Remove the ?edit=true from URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("edit");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // ✅ Handle Edit button click
  const handleOnEditClick = () => {
    setIsEditing(true);
    // Update URL query param in place (no reload)
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("edit", "true");
    searchParams.set("tab", "details");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });

    // Preload form data from current user
    setUserFormData({
      firstName: { value: user?.firstName || "", error: "" },
      lastName: { value: user?.lastName || "", error: "" },
      email: { value: user?.email || "", error: "" },
      phone: { value: user?.phone || "", error: "" },
    });
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      setIsAPiProcessing(true);
      // bodyData which will se sent to api
      const bodyData = {
        status: newStatus,
      };
      const res = await editUser(decodedId, bodyData);
      if (res.code === 200) {
        notify("success", res.message || "User status updated");
        await getUserDetails(decodedId);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to update status!";
        notify("danger", message);
      }
    } catch (e) {
      notify("danger", "Failed to update status!");
    } finally {
      setIsAPiProcessing(false);
    }
  };

  // Status Change Modal
  const StatusChangeModal = () => {
    showAlert({
      title: `Are you sure you want to ${user?.status === "ACTIVE" ? "suspend" : "activate"
        } this user?`,
      message: `User "${user?.fullName}" will be ${user?.status === "ACTIVE" ? "deactivated" : "activated"
        }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {
        handleChangeStatus(user?.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE");
      },
      onCancel: () => { },
    });
  };

  return (
    <Container fluid className="mt-4">
      {isAPiProcessing && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Breadcrumbs
        items={[
          { name: "Users", link: "/customer/user" },
          { name: `${user?.fullName}` },
        ]}
      />

      {/* Main Content */}
      <Row>
        <Card className="m-3 shadow-sm border-0">
          {showAlternative.loader ? (
            <div className="loaderContainer">
              <i className="fa fa-spinner fa-spin fa-2xl loader" />
            </div>
          ) : showAlternative.error ? (
            <div className="loaderContainer text-muted d-flex flex-column">
              <h4 className="text-muted">No User Data Available</h4>
              <span className="text-danger">{showAlternative.error}</span>
            </div>
          ) : (
            <>
              <Nav className="custom-tabs">
                <NavItem>
                  <NavLink
                    className={`text-center ${activeTab === "details" ? "active" : ""
                      }`}
                    href="#"
                    onClick={() => toggleTab("details")}
                  >
                    User Details
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`text-center ${activeTab === "assign" ? "active" : ""
                      }`}
                    href="#"
                    onClick={() => toggleTab("assign")}
                  >
                    Assigned Locations
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`text-center ${activeTab === "unassign" ? "active" : ""
                      }`}
                    href="#"
                    onClick={() => toggleTab("unassign")}
                  >
                    All Locations
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`text-center ${activeTab === "change_password" ? "active" : ""
                      }`}
                    href="#"
                    onClick={() => toggleTab("change_password")}
                  >
                    Change Password
                  </NavLink>
                </NavItem>
              </Nav>

              <CardBody>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="details">
                    <Row className="justify-content-center">
                      <Col md={12} lg={12}>
                        <Row className="d-flex justify-content-end mr-2 mb-3">
                          {!isEditing && (
                            <>
                              <Button
                                color="default"
                                size="sm"
                                onClick={handleOnEditClick}
                              >
                                <i className="fa fa-pencil mr-2" />
                                Edit
                              </Button>
                              <Button
                                color="secondary"
                                size="sm"
                                className="mr-2"
                                onClick={() => StatusChangeModal()}
                              >
                                <i
                                  className={`fa ${user?.status === "ACTIVE"
                                      ? "fa-user-slash"
                                      : "fa-user"
                                    } mr-2`}
                                />

                                {user?.status === "ACTIVE"
                                  ? "Suspend"
                                  : "Activate"}
                              </Button>
                            </>
                          )}
                        </Row>

                        <Row className="px-3">
                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label d-flex flex-column align-items-start ${userFormData.firstName.error ? "has-danger" : ""
                                }`}
                            >
                              <Label>
                                First Name{" "}
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="firstName"
                                    type="text"
                                    placeholder="Enter first name"
                                    autoComplete="off"
                                    value={userFormData.firstName.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    required
                                    maxLength={50}
                                  />
                                  {userFormData.firstName.error && (
                                    <small className="validation-error">
                                      {userFormData.firstName.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text text-wrap">
                                  {user?.firstName}
                                </span>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label d-flex flex-column align-items-start ${userFormData.lastName.error ? "has-danger" : ""
                                }`}
                            >
                              <Label>
                                Last Name{" "}
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="lastName"
                                    type="text"
                                    placeholder="Enter last name"
                                    autoComplete="off"
                                    value={userFormData.lastName.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    required
                                    maxLength={50}
                                  />
                                  {userFormData.lastName.error && (
                                    <small className="validation-error">
                                      {userFormData.lastName.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text text-wrap">
                                  {user?.lastName}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label d-flex flex-column align-items-start ${userFormData.email.error ? "has-danger" : ""
                                }`}
                            >
                              <Label>
                                Email Address{" "}
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter email address"
                                    autoComplete="off"
                                    value={userFormData.email.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    required
                                    maxLength={100}
                                  />
                                  {userFormData.email.error && (
                                    <small className="validation-error">
                                      {userFormData.email.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text text-wrap">
                                  {user?.email}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label d-flex flex-column align-items-start ${
                                userFormData.phone.error ? "has-danger" : ""
                              }`}
                            >
                              <Label>
                                Contact Number{" "}
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <InputGroup className="input-group">
                                    <InputGroupAddon addonType="prepend">
                                      <InputGroupText className="d-flex align-items-center">
                                        <span className="mr-1">🇸🇦</span> +966
                                      </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                      name="phone"
                                      type="text"
                                      placeholder="Enter contact number"
                                      autoComplete="off"
                                      value={userFormData.phone.value}
                                      onChange={(e) =>
                                        handleInputChange(
                                          e.target.name,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={(e) =>
                                        handleOnBlur(
                                          e.target.name,
                                          e.target.value,
                                        )
                                      }
                                      required
                                      maxLength={9}
                                    />
                                  </InputGroup>
                                  {userFormData.phone.error && (
                                    <small className="validation-error">
                                      {userFormData.phone.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text text-wrap">
                                  {user?.phone}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>Status</Label>
                              <Badge
                                className="px-3 py-2"
                                color={
                                  user?.status === "ACTIVE"
                                    ? "success"
                                    : "warning"
                                }
                                pill
                              >
                                <i className="tim-icons icon-bag mr-1" />
                                {user?.status}
                              </Badge>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col className="d-flex justify-content-end">
                            {isEditing && (
                              <>
                                <Button
                                  className="mr-2"
                                  color="secondary"
                                  size="sm"
                                  onClick={handleCancel}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  color="default"
                                  size="sm"
                                  onClick={() => {
                                    handleSaveClick();
                                  }}
                                  disabled={Object.values(userFormData).some(
                                    (item) => item.error !== "",
                                  )}
                                >
                                  {showLoaderOnClick ? (
                                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                                  ) : (
                                    "Save"
                                  )}
                                </Button>
                              </>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId="change_password">
                    <Row className="justify-content-start my-3 px-3">
                      <Col md="4">
                        <FormGroup
                          className={`has-label ${userPasswordData.password.error ? "has-danger" : ""
                            } d-flex flex-column align-items-start mb-4`}
                        >
                          <Label>
                            New Password{" "}
                            <span className="validation-error">*</span>
                          </Label>
                          <InputGroup className="input-group">
                            <Input
                              name="password"
                              type={showPassword ? "text" : "password"}
                              autoComplete="off"
                              placeholder="Enter new password"
                              value={userPasswordData.password.value}
                              onChange={(e) =>
                                handlePasswordInputChange(
                                  e.target.name,
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleOnBlurPassForm(
                                  e.target.name,
                                  e.target.value,
                                )
                              }
                              required
                              maxLength={16}
                            />
                            <InputGroupAddon addonType="append">
                              <InputGroupText
                                className="d-flex align-items-center pointer"
                                onClick={() => setShowPassword((prev) => !prev)}
                              >
                                {showPassword ? (
                                  <i className="fa fa-eye-slash" />
                                ) : (
                                  <i className="fa fa-eye" />
                                )}
                              </InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          {userPasswordData.password.error && (
                            <small className="validation-error">
                              {userPasswordData.password.error}
                            </small>
                          )}
                        </FormGroup>
                        <FormGroup
                          className={`has-label ${userPasswordData.confirmPassword.error
                              ? "has-danger"
                              : ""
                            } d-flex flex-column align-items-start mb-4`}
                        >
                          <Label>
                            Confirm New Password{" "}
                            <span className="validation-error">*</span>
                          </Label>
                          <InputGroup className="input-group">
                            <Input
                              name="confirmPassword"
                              type={showConfirmPassword ? "text" : "password"}
                              autoComplete="off"
                              placeholder="Confirm new password"
                              value={userPasswordData.confirmPassword.value}
                              onChange={(e) =>
                                handlePasswordInputChange(
                                  e.target.name,
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleOnBlurPassForm(
                                  e.target.name,
                                  e.target.value,
                                )
                              }
                              required
                              maxLength={16}
                            />
                            <InputGroupAddon addonType="append">
                              <InputGroupText
                                className="d-flex align-items-center pointer"
                                onClick={() =>
                                  setShowConfirmPassword((prev) => !prev)
                                }
                              >
                                {showConfirmPassword ? (
                                  <i className="fa fa-eye-slash" />
                                ) : (
                                  <i className="fa fa-eye" />
                                )}
                              </InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          {userPasswordData.confirmPassword.error && (
                            <small className="validation-error">
                              {userPasswordData.confirmPassword.error}
                            </small>
                          )}
                        </FormGroup>
                        <div className="d-flex justify-content-start">
                          <Button
                            color="default"
                            type="submit"
                            className="px-4 shadow-sm"
                            onClick={() => {
                              handlePasswordSaveClick();
                            }}
                            disabled={Object.values(userPasswordData).some(
                              (item) => item.error !== "",
                            )}
                          >
                            {showLoaderPasswordSave ? (
                              <i className="fa fa-spinner fa-spin fa-lg text-white" />
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="assign">
                    {activeTab === "assign" && (
                      <LocationTable
                        activeTabId="assign"
                        decodedId={decodedId}
                        notify={notify}
                        assignedLocationIds={assignedLocationIds}
                        setAssignedLocationIds={setAssignedLocationIds}
                      />
                    )}
                  </TabPane>

                  <TabPane tabId="unassign">
                    {activeTab === "unassign" && (
                      <LocationTable
                        activeTabId="unassign"
                        decodedId={decodedId}
                        notify={notify}
                        assignedLocationIds={assignedLocationIds}
                        setAssignedLocationIds={setAssignedLocationIds}
                      />
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </>
          )}
        </Card>
      </Row>
    </Container>
  );
};

export default UserDetails;
